// ConfigurationModal.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { CONFIGURATION_GLOBAL_URL } from "../apiConfig";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
  Tabs,
  Tab,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Visibility } from "@mui/icons-material";

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const ConfigurationModal = ({
  open,
  fields,
  selectedFields,
  onFieldToggle,
  onApply,
  onCancel,
}) => {
  const qc = useQueryClient();
  const [tab, setTab] = useState(0);
  const [inicioAcceso, setInicioAcceso] = useState("");
  const [grupoGlobal, setGrupoGlobal] = useState('A'); // Nuevo estado para grupoGlobal
  const [localError, setLocalError] = useState(null);
  const [userRole, setUserRole] = useState('supervision'); // Estado para el rol del usuario

  useEffect(() => {
    setUserRole(localStorage.getItem('is_administrator') === 'true' ? 'admin' : 'supervision') // Determina el rol del usuario
    // console.log ('userRole:', userRole); // Verifica el rol del usuario en la consola

  }, [localStorage.getItem('is_administrator')]); 
  
  // --- fetch config (v5 signature)
  const { data, isLoading, error, refetch } = useQuery({
  queryKey: ["configuracion-global"],
  queryFn: async () => {
    const { data } = await axios.get(CONFIGURATION_GLOBAL_URL, {
      headers: { Authorization: `Token ${localStorage.getItem("token")}` },
    });
    return data;
  },
  enabled: !!open && userRole === "admin", // 🔹 Solo corre si es admin
  staleTime: 30000,
  onError: (err) => {
    console.error("Error cargando configuración:", err);
    toast.error("No se pudo cargar la configuración global.");
  },
});

useEffect(() => {
  if (open && data) {
    setInicioAcceso(data.inicio_acceso || "");
    setGrupoGlobal(data.grupo_global || 'A'); // Inicializa grupoGlobal con el valor del servidor
  }

  if (!open) {
    setTab(0);
    setLocalError(null);
  }
}, [open, data]);

const mutation = useMutation({
  mutationFn: async (payload) => {
    if (userRole !== "admin") return; // 🔹 Evita request si no es admin
    const { data } = await axios.patch(CONFIGURATION_GLOBAL_URL, payload, {
      headers: { Authorization: `Token ${localStorage.getItem("token")}` },
    });
    return data;
  },
  onSuccess: () => {
    qc.invalidateQueries({ queryKey: ["configuracion-global"] });
    refetch();
    toast.success("Fecha de inicio guardada.");
  },
  onError: (err) => {
    console.error("Error guardando configuración:", err);
    toast.error("No se pudo guardar la fecha de inicio.");
    setLocalError("No se pudo guardar la fecha.");
  },
});


  const handleSaveDate = () => {
    setLocalError(null);
    const payload = { inicio_acceso: inicioAcceso ? inicioAcceso : null, grupo_global: grupoGlobal ? grupoGlobal : 'A' }; 
    mutation.mutate(payload);
  };

  const handleClearDate = () => setInicioAcceso("");

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span>Configuraciones</span>
        <IconButton size="small" onClick={onCancel}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading ? (
          <Grid container justifyContent="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Grid>
        ) : error && (userRole === 'admin') ? (
          <Typography color="error">No se pudo cargar la configuración.</Typography>
        ) : (
          <>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} aria-label="config tabs">
              <Tab label="Campos" />
              {userRole === "admin" && <Tab label="Inicio de acceso" />}
            </Tabs>

            <TabPanel value={tab} index={0}>
              <Grid container spacing={1}>
                {fields.map((field) => (
                  <Grid item xs={12} sm={6} md={4} key={field}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedFields.includes(field)}
                          onChange={() => onFieldToggle(field)}
                        />
                      }
                      label={field}
                    />
                  </Grid>
                ))}
              </Grid>
            </TabPanel>
            
            {userRole === "admin" && (
              <TabPanel value={tab} index={1}>
                <Grid container spacing={2} alignItems="center">

                <div className="d-flex flex-row border p-3 mb-3">
                  <Grid item xs={12} className="mx-3">
                    <TextField
                      label="Inicio de acceso (global)"
                      type="date"
                      fullWidth
                      value={inicioAcceso || ""}
                      onChange={(e) => setInicioAcceso(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      helperText="Formato: MM-DD-YYYY"
                    />
                  </Grid>

                  {/* <Grid item xs={6} sm={4} className="me-5">
                    <Button variant="outlined" fullWidth onClick={handleClearDate} disabled={mutation.isLoading}>
                      Limpiar
                    </Button>
                  </Grid> */}

                  

                  <Grid item xs={12}>
                    {localError && <Typography color="error">{localError}</Typography>}
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      select
                      label="Grupo Global"
                      value={grupoGlobal}
                      onChange={(e) => setGrupoGlobal(e.target.value)}
                      fullWidth
                      variant="outlined"
                      helperText="Selecciona el grupo global"
                      SelectProps={{ native: true }}
                    >
                      <option value="A">A</option>
                      <option value="B">B</option>
                      <option value="C">C</option>
                      <option value="D">D</option>
                    </TextField>
                  </Grid>

                  </div>
                  <Grid item xs={6} sm={4} className="mx-3">
                    <Button variant="contained" fullWidth onClick={handleSaveDate} disabled={mutation.isLoading}>
                      {mutation.isLoading ? <CircularProgress size={20} /> : "Guardar datos de inicio"}
                    </Button>
                  </Grid>
                </Grid>
              </TabPanel>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel}>Cancelar</Button>
        <Button onClick={onApply} variant="contained">Aplicar campos</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfigurationModal;
